/**
 * Scroll-reveal utility.
 *
 * Watches elements with [data-reveal] and adds .is-revealed when they
 * enter the viewport. Child elements with [data-animate] inside a
 * [data-reveal] container get .is-visible when the parent reveals.
 *
 * Standalone [data-animate] elements (no [data-reveal] parent) are
 * observed individually.
 *
 * Usage in render.php:
 *
 *   <!-- Block-level reveal with staggered children -->
 *   <section data-reveal>
 *     <h1 data-animate="fade-up">Heading</h1>
 *     <p data-animate="fade-up" style="--stagger: 1">Copy</p>
 *     <a data-animate="fade-up" style="--stagger: 2">CTA</a>
 *   </section>
 *
 *   <!-- Single element reveal (no parent needed) -->
 *   <img data-animate="fade-up" src="..." />
 *
 * Respects prefers-reduced-motion — skips observation entirely.
 */

(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    // Make everything visible immediately
    document.querySelectorAll('[data-animate]').forEach(function (el) {
      el.classList.add('is-visible');
    });
    document.querySelectorAll('[data-reveal]').forEach(function (el) {
      el.classList.add('is-revealed');
    });
    return;
  }

  // Negative bottom margin shrinks the observer viewport from below,
  // so elements only intersect once they scroll into the top portion.
  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        revealElement(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0 },
  );

  // Shared reveal logic — adds is-visible and checks parent data-reveal
  function revealElement(el) {
    if (el.classList.contains('is-visible')) return;
    el.classList.add('is-visible');

    var parent = el.closest('[data-reveal]');
    if (parent && !parent.classList.contains('is-revealed')) {
      var allVisible = Array.prototype.every.call(
        parent.querySelectorAll('[data-animate]'),
        function (child) {
          return child.classList.contains('is-visible');
        },
      );
      if (allVisible) {
        parent.classList.add('is-revealed');
      }
    }
  }

  // Fallback observer for elements near the page bottom that can never
  // reach the primary trigger line (e.g. footer CTA).
  var fallbackObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        revealElement(entry.target);
        fallbackObserver.unobserve(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.1 },
  );

  var triggerOffset = window.innerHeight * 0.1;

  document.querySelectorAll('[data-animate]').forEach(function (el) {
    observer.observe(el);

    // If element is close to the document bottom, it can't scroll far
    // enough to hit the primary trigger. Watch it with the fallback too.
    var elTop = el.getBoundingClientRect().top + window.scrollY;
    var distFromBottom = document.documentElement.scrollHeight - elTop;
    if (distFromBottom < window.innerHeight + triggerOffset) {
      fallbackObserver.observe(el);
    }
  });
})();
